import React, { useState, useEffect } from 'react'
import {
  generateAccessibilityMap,
  getAccessibilityMapOptions,
  pngBase64ToDataUrl,
  type AccessibilityMapInputs,
} from '../lib/sb-api-client'
import { Sidebar } from '../components/layout/Sidebar'
import { useAuth } from '../lib/auth'

interface Province {
  id: string
  label: string
}

interface Municipality {
  id: string
  label: string
  statnaam: string
}

interface RegionType {
  id: string
  label: string
  label_en: string
  description: string
}

interface Amenity {
  id: string
  label: string
  label_en: string
}

interface AmenityCategory {
  label: string
  label_en: string
  amenities: Amenity[]
}

interface TransportMode {
  id: string
  label: string
  label_en: string
  description: string
}

interface MapType {
  id: string
  label: string
  label_en: string
  description: string
  description_en?: string
}

interface Options {
  region_types: RegionType[]
  provinces: Province[]
  municipalities: Municipality[]
  amenities_by_category: Record<string, AmenityCategory>
  transport_modes: TransportMode[]
  map_types: MapType[]
}

interface GeneratedMap {
  id: string
  status: string
  region_type: string
  region_id?: string
  region_label?: string
  amenity: string
  transport_mode: string
  map_type: string
  table_name: string
  output_url?: string
  processing_time_ms?: number
  error_message?: string
}

export default function AccessibilityMaps() {
  const { user } = useAuth()

  // Form state
  const [selectedRegionType, setSelectedRegionType] = useState('province')
  const [selectedRegionId, setSelectedRegionId] = useState('')
  const [selectedAmenity, setSelectedAmenity] = useState('')
  const [selectedMode, setSelectedMode] = useState('')
  const [selectedMapType, setSelectedMapType] = useState('geen_keuze')
  const [includeAmenityDots, setIncludeAmenityDots] = useState(true)

  // UI state
  const [loading, setLoading] = useState(false)
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showMethodology, setShowMethodology] = useState(false)
  const [municipalitySearch, setMunicipalitySearch] = useState('')

  // Data
  const [options, setOptions] = useState<Options | null>(null)
  const [generatedMap, setGeneratedMap] = useState<GeneratedMap | null>(null)

  // Load options on mount
  useEffect(() => {
    loadOptions()
  }, [])

  // Reset region_id and search when region type changes
  useEffect(() => {
    setSelectedRegionId('')
    setMunicipalitySearch('')
  }, [selectedRegionType])

  const loadOptions = async () => {
    try {
      setOptionsLoading(true)
      const data = await getAccessibilityMapOptions()
      setOptions(data)
      setError(null)
    } catch (err) {
      console.error('Failed to load options:', err)
      setError('Failed to load form options. Please refresh the page.')
    } finally {
      setOptionsLoading(false)
    }
  }

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setGeneratedMap(null)

    const inputs: AccessibilityMapInputs = {
      mode: selectedMode,
      amenity: selectedAmenity,
      region_type: selectedRegionType,
      region_id: selectedRegionType !== 'national' ? selectedRegionId : undefined,
      map_type: selectedMapType,
      include_amenity_dots: includeAmenityDots,
    }

    try {
      const ogcResult = await generateAccessibilityMap(inputs)
      const status = ogcResult.metadata?.status || 'completed'
      // Adapt OGC response shape -> the legacy GeneratedMap shape the JSX expects
      const adapted: GeneratedMap = {
        id: ogcResult.table_name || `${Date.now()}`,
        status,
        region_type: selectedRegionType,
        region_id: selectedRegionType !== 'national' ? selectedRegionId : undefined,
        region_label: ogcResult.metadata?.region_label,
        amenity: selectedAmenity,
        transport_mode: selectedMode,
        map_type: selectedMapType,
        table_name: ogcResult.table_name,
        output_url: pngBase64ToDataUrl(ogcResult.image),
        processing_time_ms: ogcResult.metadata?.processing_time_ms,
        error_message: ogcResult.metadata?.error_message,
      }
      setGeneratedMap(adapted)
      if (status === 'failed') {
        setError(adapted.error_message || 'Map generation failed')
      }
    } catch (err) {
      console.error('Generation failed:', err)
      const message = err instanceof Error ? err.message : 'Map generation failed. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async () => {
    if (!generatedMap?.output_url) return
    try {
      // Chrome blocks navigating to / downloading a large `data:` URL directly
      // ("Not allowed to navigate top frame to data URL"). Convert it to a Blob
      // and download via a short-lived object URL, which is always permitted.
      const res = await fetch(generatedMap.output_url)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${generatedMap.table_name || 'accessibility-map'}.png`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download failed:', err)
    }
  }

  const canGenerate =
    selectedAmenity &&
    selectedMode &&
    selectedMapType &&
    (selectedRegionType === 'national' || selectedRegionId) &&
    !loading

  if (optionsLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar activeItem="tools" user={user} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-sb-benzol-green border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading options...</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar activeItem="tools" user={user} />

      <main id="main-content" className="flex-1 overflow-auto px-8 py-10">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-sb-black mb-2">Accessibility Maps</h1>
          <p className="text-base text-sb-grey mb-3">
            Bereikbaarheid op Peil — Kunnen mensen hun dagelijkse leven goed organiseren?
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Landelijke bereikbaarheidsanalyses voor onderwijs, zorg en dagelijkse boodschappen.
            Per vervoerswijze, vergeleken met acceptabele reistijden (KiM-onderzoek).
          </p>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {/* Generate Map Form */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Generate Map</h2>

            <form onSubmit={handleGenerate} className="space-y-6">
              {/* Region Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Region Type
                </label>
                <div className="flex flex-wrap gap-3">
                  {options?.region_types.map((rt) => (
                    <label
                      key={rt.id}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                        selectedRegionType === rt.id
                          ? 'border-sb-benzol-green bg-green-50 text-green-800'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="regionType"
                        value={rt.id}
                        checked={selectedRegionType === rt.id}
                        onChange={(e) => setSelectedRegionType(e.target.value)}
                        className="sr-only"
                        disabled={loading}
                      />
                      <span className="text-sm font-medium">{rt.label}</span>
                    </label>
                  ))}
                </div>
                {selectedRegionType && options?.region_types.find(rt => rt.id === selectedRegionType)?.description && (
                  <p className="mt-2 text-xs text-gray-500">
                    {options.region_types.find(rt => rt.id === selectedRegionType)?.description}
                  </p>
                )}
              </div>

              {/* Province Selection (shown for province type) */}
              {selectedRegionType === 'province' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Province
                  </label>
                  <select
                    value={selectedRegionId}
                    onChange={(e) => setSelectedRegionId(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sb-benzol-green focus:border-transparent bg-white"
                    required
                    disabled={loading}
                  >
                    <option value="">Select a province...</option>
                    {options?.provinces.map((prov) => (
                      <option key={prov.id} value={prov.id}>
                        {prov.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Municipality Selection (shown for municipality type) */}
              {selectedRegionType === 'municipality' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Municipality
                  </label>
                  {/* Search input */}
                  <input
                    type="text"
                    value={municipalitySearch}
                    onChange={(e) => setMunicipalitySearch(e.target.value)}
                    placeholder="Type to search municipalities..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sb-benzol-green focus:border-transparent bg-white mb-2"
                    disabled={loading}
                  />
                  <select
                    value={selectedRegionId}
                    onChange={(e) => {
                      setSelectedRegionId(e.target.value)
                      // Clear search after selection
                      const selected = options?.municipalities.find(m => m.id === e.target.value)
                      if (selected) setMunicipalitySearch(selected.label)
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sb-benzol-green focus:border-transparent bg-white"
                    required
                    disabled={loading}
                    size={Math.min(10, (options?.municipalities.filter(m =>
                      municipalitySearch === '' ||
                      m.label.toLowerCase().includes(municipalitySearch.toLowerCase())
                    ).length || 0) + 1)}
                  >
                    <option value="">Select a municipality...</option>
                    {options?.municipalities
                      .filter(mun =>
                        municipalitySearch === '' ||
                        mun.label.toLowerCase().includes(municipalitySearch.toLowerCase())
                      )
                      .map((mun) => (
                        <option key={mun.id} value={mun.id}>
                          {mun.label}
                        </option>
                      ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {municipalitySearch && options?.municipalities.filter(m =>
                      m.label.toLowerCase().includes(municipalitySearch.toLowerCase())
                    ).length} of {options?.municipalities.length || 0} municipalities shown •
                    Maps include detailed base layers (buildings, local roads)
                  </p>
                </div>
              )}

              {/* Amenity Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amenity Type
                </label>
                <select
                  value={selectedAmenity}
                  onChange={(e) => setSelectedAmenity(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sb-benzol-green focus:border-transparent bg-white"
                  required
                  disabled={loading}
                >
                  <option value="">Select an amenity type...</option>
                  {options && Object.entries(options.amenities_by_category).map(([catId, category]) => (
                    <optgroup key={catId} label={category.label}>
                      {category.amenities.map((amenity) => (
                        <option key={amenity.id} value={amenity.id}>
                          {amenity.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Transport Mode Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transport Mode
                </label>
                <select
                  value={selectedMode}
                  onChange={(e) => setSelectedMode(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sb-benzol-green focus:border-transparent bg-white"
                  required
                  disabled={loading}
                >
                  <option value="">Select a transport mode...</option>
                  {options?.transport_modes.map((mode) => (
                    <option key={mode.id} value={mode.id}>
                      {mode.label}
                    </option>
                  ))}
                </select>
                {selectedMode && options?.transport_modes.find(m => m.id === selectedMode)?.description && (
                  <p className="mt-1 text-xs text-gray-500">
                    {options.transport_modes.find(m => m.id === selectedMode)?.description}
                  </p>
                )}
              </div>

              {/* Map Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Map Type
                </label>
                <div className="space-y-2">
                  {options?.map_types.map((mapType) => (
                    <label key={mapType.id} className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="mapType"
                        value={mapType.id}
                        checked={selectedMapType === mapType.id}
                        onChange={(e) => setSelectedMapType(e.target.value)}
                        className="mt-1 w-4 h-4 text-sb-benzol-green focus:ring-sb-benzol-green"
                        disabled={loading}
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{mapType.label}</span>
                        <p className="text-xs text-gray-500">{mapType.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Options */}
              <div className="pt-2 border-t border-gray-100">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeAmenityDots}
                    onChange={(e) => setIncludeAmenityDots(e.target.checked)}
                    className="w-4 h-4 text-sb-benzol-green rounded focus:ring-sb-benzol-green"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-700">Include amenity location dots on map</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={!canGenerate}
                className="w-full bg-sb-benzol-green text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#70C282] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating Map{selectedRegionType === 'national' ? ' (this may take a few minutes)' : '...'}
                  </span>
                ) : (
                  'Generate Map'
                )}
              </button>
            </form>
          </div>

          {/* Generated Map Result */}
          {generatedMap && generatedMap.status === 'completed' && generatedMap.output_url && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Generated Map</h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                <div>
                  <p className="text-gray-500">Region</p>
                  <p className="font-medium">{generatedMap.region_label || 'Nederland'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Amenity</p>
                  <p className="font-medium">
                    {Object.values(options?.amenities_by_category || {})
                      .flatMap(c => c.amenities)
                      .find(a => a.id === generatedMap.amenity)?.label}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Transport</p>
                  <p className="font-medium">{options?.transport_modes.find(m => m.id === generatedMap.transport_mode)?.label}</p>
                </div>
                <div>
                  <p className="text-gray-500">Processing Time</p>
                  <p className="font-medium">{((generatedMap.processing_time_ms || 0) / 1000).toFixed(1)}s</p>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden mb-4 bg-gray-100">
                <img
                  src={generatedMap.output_url}
                  alt={`Accessibility map for ${generatedMap.region_label || 'Nederland'}`}
                  className="w-full h-auto"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 bg-sb-antwerp-blue text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#056B7F] transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download PNG
                </button>
                <button
                  onClick={() => setGeneratedMap(null)}
                  className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Generate Another
                </button>
              </div>
            </div>
          )}

          {/* Methodology Info Box */}
          <div className="bg-[#F7FAE9] border border-[#C5D967] rounded-lg overflow-hidden mb-6">
            <button
              onClick={() => setShowMethodology(!showMethodology)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-[#EEF5D8] transition-colors"
            >
              <h3 className="font-semibold text-[#6B8E23]">Bereikbaarheid op Peil — Methodologie</h3>
              <svg
                className={`w-5 h-5 text-[#6B8E23] transition-transform ${showMethodology ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showMethodology && (
              <div className="px-4 pb-4 border-t border-[#C5D967]">
                <div className="pt-4 space-y-4 text-sm text-gray-700">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Wat meet dit?</h4>
                    <p>
                      Bereikbaarheid is een optelsom van drie dingen: <strong>woonlocaties</strong>, <strong>voorzieningen</strong> en <strong>netwerken</strong>.
                      Per vervoerswijze wordt de reistijd naar de dichtstbijzijnde voorziening vergeleken met de
                      gemiddeld acceptabele reistijd (op basis van KiM-onderzoek).
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Wat is beschikbaar?</h4>
                    <p className="mb-2">Vanuit elke straat in Nederland (500m-grid) is berekend:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Reistijd naar dichtstbijzijnde voorziening</li>
                      <li>Vergelijking met acceptabele reistijd</li>
                      <li>Hoeveel voorzieningen bereikbaar zijn (keuze)</li>
                      <li>Bereikbare banen</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Legenda</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="inline-block w-3 h-3 rounded-full bg-red-500 mt-0.5 flex-shrink-0"></span>
                        <div>
                          <strong>Buiten bereik / Geen keuze (rood)</strong> — Inwoners met slechts 1 voorziening
                          binnen de acceptabele reistijd. Afhankelijk van dichtstbijzijnde — kwetsbaar bij sluiting.
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="inline-block w-3 h-3 rounded-full bg-yellow-400 mt-0.5 flex-shrink-0"></span>
                        <div>
                          <strong>Binnen bereik / Wel keuze (geel)</strong> — Inwoners met 2+ voorzieningen
                          binnen de acceptabele reistijd. Alternatieven beschikbaar.
                        </div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="inline-block w-4 h-3 bg-gradient-to-r from-green-400 via-yellow-400 to-red-500 rounded mt-0.5 flex-shrink-0"></span>
                        <div>
                          <strong>Reistijd (kleurverloop)</strong> — Reistijd in minuten naar dichtstbijzijnde
                          voorziening. Hoe donkerder de kleur, hoe meer minuten.
                        </div>
                      </li>
                    </ul>
                    <p className="mt-2 text-xs text-gray-500">
                      Stip-grootte = aantal inwoners (grotere stip = meer inwoners in 500m-zone)
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Kaarttypen</h4>
                    <ul className="space-y-2">
                      <li>
                        <strong>Buiten bereik (geen keuze)</strong> — Laat zien welke gebieden afhankelijk zijn
                        van slechts 1 voorziening. Kwetsbaar bij sluiting of verandering.
                      </li>
                      <li>
                        <strong>Binnen bereik (wel keuze)</strong> — Laat zien welke gebieden meerdere
                        voorzieningen kunnen bereiken binnen de acceptabele reistijd.
                      </li>
                      <li>
                        <strong>Reistijd</strong> — Daadwerkelijke reistijd in minuten naar dichtstbijzijnde voorziening.
                      </li>
                      <li>
                        <strong>Mandje</strong> — Gecombineerde score voor basisvoorzieningen
                        (basisschool + voortgezet onderwijs + huisarts + supermarkt).
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Acceptabele reistijden per voorziening (minuten)</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-white">
                            <th className="border border-gray-200 px-2 py-1 text-left">Voorziening</th>
                            <th className="border border-gray-200 px-2 py-1 text-center">Fiets</th>
                            <th className="border border-gray-200 px-2 py-1 text-center">OV</th>
                            <th className="border border-gray-200 px-2 py-1 text-center">Auto</th>
                            <th className="border border-gray-200 px-2 py-1 text-center">Lopen</th>
                            <th className="border border-gray-200 px-2 py-1 text-center">Keuze</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr><td className="border border-gray-200 px-2 py-1">Basisonderwijs (6.097)</td><td className="border border-gray-200 px-2 py-1 text-center">12</td><td className="border border-gray-200 px-2 py-1 text-center">12</td><td className="border border-gray-200 px-2 py-1 text-center">11</td><td className="border border-gray-200 px-2 py-1 text-center">12</td><td className="border border-gray-200 px-2 py-1 text-center">2</td></tr>
                          <tr className="bg-gray-50"><td className="border border-gray-200 px-2 py-1">Voortgezet onderwijs (1.527)</td><td className="border border-gray-200 px-2 py-1 text-center">17</td><td className="border border-gray-200 px-2 py-1 text-center">32</td><td className="border border-gray-200 px-2 py-1 text-center">27</td><td className="border border-gray-200 px-2 py-1 text-center">23</td><td className="border border-gray-200 px-2 py-1 text-center">2</td></tr>
                          <tr><td className="border border-gray-200 px-2 py-1">MBO (504)</td><td className="border border-gray-200 px-2 py-1 text-center">24</td><td className="border border-gray-200 px-2 py-1 text-center">41</td><td className="border border-gray-200 px-2 py-1 text-center">26</td><td className="border border-gray-200 px-2 py-1 text-center">41</td><td className="border border-gray-200 px-2 py-1 text-center">2</td></tr>
                          <tr className="bg-gray-50"><td className="border border-gray-200 px-2 py-1">HBO (64)</td><td className="border border-gray-200 px-2 py-1 text-center">36</td><td className="border border-gray-200 px-2 py-1 text-center">47</td><td className="border border-gray-200 px-2 py-1 text-center">28</td><td className="border border-gray-200 px-2 py-1 text-center">21</td><td className="border border-gray-200 px-2 py-1 text-center">2</td></tr>
                          <tr><td className="border border-gray-200 px-2 py-1">Universiteit (21)</td><td className="border border-gray-200 px-2 py-1 text-center">36</td><td className="border border-gray-200 px-2 py-1 text-center">46</td><td className="border border-gray-200 px-2 py-1 text-center">25</td><td className="border border-gray-200 px-2 py-1 text-center">16</td><td className="border border-gray-200 px-2 py-1 text-center">2</td></tr>
                          <tr className="bg-gray-50"><td className="border border-gray-200 px-2 py-1">Ziekenhuis/SEH (76)</td><td className="border border-gray-200 px-2 py-1 text-center">22</td><td className="border border-gray-200 px-2 py-1 text-center">32</td><td className="border border-gray-200 px-2 py-1 text-center">24</td><td className="border border-gray-200 px-2 py-1 text-center">22</td><td className="border border-gray-200 px-2 py-1 text-center">2</td></tr>
                          <tr><td className="border border-gray-200 px-2 py-1">Huisarts (4.837)</td><td className="border border-gray-200 px-2 py-1 text-center">12</td><td className="border border-gray-200 px-2 py-1 text-center">18</td><td className="border border-gray-200 px-2 py-1 text-center">13</td><td className="border border-gray-200 px-2 py-1 text-center">15</td><td className="border border-gray-200 px-2 py-1 text-center">2</td></tr>
                          <tr className="bg-gray-50"><td className="border border-gray-200 px-2 py-1">Supermarkt (4.206)</td><td className="border border-gray-200 px-2 py-1 text-center">12</td><td className="border border-gray-200 px-2 py-1 text-center">15</td><td className="border border-gray-200 px-2 py-1 text-center">12</td><td className="border border-gray-200 px-2 py-1 text-center">13</td><td className="border border-gray-200 px-2 py-1 text-center">2</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      <strong>Keuze</strong> = minimaal aantal voorzieningen binnen acceptabele reistijd om keuzevrijheid te hebben.
                      Getallen tussen haakjes = totaal aantal voorzieningen in Nederland.
                      Bron: KiM-onderzoek naar acceptabele reistijden.
                    </p>
                  </div>

                  <div className="pt-2 border-t border-[#C5D967]">
                    <p className="text-xs text-gray-500">
                      <strong>Data:</strong> V500-grid (500m resolutie), geaggregeerd naar buurt/wijk/gemeente/provincie.
                      Bereikbaarheid op Peil, IenW — Studio Bereikbaar.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Help Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">Tips</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>Landelijke kaarten kunnen enkele minuten duren vanwege datavolume</li>
              <li>Gemeente-kaarten bevatten gedetailleerde basislagen (gebouwen, lokale wegen)</li>
              <li>"Banen" ondersteunt alleen het bereikbaarheidskaarttype</li>
              <li>Kaarten worden gegenereerd op 300 DPI, geschikt voor print en rapporten</li>
              <li>Mis je een voorziening of kaartbeeld? Neem contact op met Susan of Gio</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  )
}
